import { useState } from 'react';
import { DeleteButton, EmployeePicker, ErrorBanner, Field, Loading, Modal } from '../../components/ui';
import { RockHerald } from '../../components/RockHerald';
import { api } from '../../lib/api';
import { formatDate } from '../../lib/format';
import { useResource } from '../../lib/hooks';
import type { Employee } from '../../lib/types';
import type { EosRock, RockStatus } from './eosTypes';
import type { BusinessUnitOption } from './eosUi';
import {
  ROCK_STATUS_LABELS,
  BusinessUnitPicker,
  eosQuarter,
  quarterOptions,
  rockBand,
} from './eosUi';

const STATUSES: RockStatus[] = ['not_started', 'on_track', 'off_track', 'complete', 'incomplete'];

/**
 * Rocks — the quarter's 90-day priorities.
 *
 * These are `coo.Rock`, the SAME rocks as the V/TO's Rocks section. A rock set
 * here appears on the plan and a rock set on the plan is reviewed here, which
 * is the whole reason migration 058 extended that table rather than adding a
 * second one. Worth knowing before wondering why a rock you did not create is
 * on the list.
 *
 * Company rocks show on every team's list, because a company rock is reviewed
 * in every L10 — that is what makes it a company rock.
 */
export function EosRocks({ teamId, onChanged }: { teamId: number; onChanged: () => void }) {
  const [quarter, setQuarter] = useState(eosQuarter());
  const rocks = useResource<EosRock[]>(`/eos/rocks?teamId=${teamId}&quarter=${quarter}`);
  const employees = useResource<Employee[]>('/employees?status=active');
  const [editing, setEditing] = useState<EosRock | null>(null);
  const [adding, setAdding] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /* The title of a rock just marked done, while the herald proclaims it. Set
     only after the save succeeds and only on the move INTO done — a rock that
     loads as done, or is saved again while already done, gets no fanfare. */
  const [heralding, setHeralding] = useState<string | null>(null);

  const patch = async (rock: EosRock, body: Record<string, unknown>) => {
    setError(null);
    try {
      await api.put(`/eos/rocks/${rock.RockId}`, body);
      if (body.status === 'complete' && rock.Status !== 'complete') setHeralding(rock.Title);
      rocks.reload();
      onChanged();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save that change.');
    }
  };

  return (
    <>
      {error && <ErrorBanner message={error} />}

      <section className="card">
        <div className="card-head row-between wrap">
          <div>
            <h3>Rocks</h3>
            <p className="xs subtle">
              The 90-day priorities for this quarter. These are the same rocks as the V/TO&apos;s —
              a rock set here is on the plan.
            </p>
          </div>
          <div className="row wrap" style={{ gap: 8 }}>
            <select className="select" value={quarter} onChange={(e) => setQuarter(e.target.value)}>
              {quarterOptions().map((q) => (
                <option key={q} value={q}>
                  {q}
                </option>
              ))}
            </select>
            <button type="button" className="btn btn-primary btn-sm" onClick={() => setAdding(true)}>
              Add rock
            </button>
          </div>
        </div>
        <div className="card-body">
        {rocks.loading && <Loading label="Loading rocks" />}
        {rocks.error && <ErrorBanner message={rocks.error} />}
        {rocks.data?.length === 0 && <p className="muted">No rocks set for {quarter} yet.</p>}

        {rocks.data?.map((rock) => (
          <RockRow
            key={rock.RockId}
            rock={rock}
            onStatus={(status) => void patch(rock, { status })}
            onEdit={() => setEditing(rock)}
            onDelete={() => {
              void api
                .del(`/eos/rocks/${rock.RockId}`)
                .then(() => {
                  rocks.reload();
                  onChanged();
                })
                .catch((err: unknown) =>
                  setError(err instanceof Error ? err.message : 'Could not delete it.'),
                );
            }}
            onMilestonesChanged={() => rocks.reload()}
            onError={setError}
          />
        ))}
        </div>
      </section>

      {(adding || editing) && (
        <RockForm
          teamId={teamId}
          quarter={quarter}
          employees={employees.data ?? []}
          rock={editing}
          onClose={() => {
            setAdding(false);
            setEditing(null);
          }}
          onSaved={(doneTitle) => {
            if (doneTitle) setHeralding(doneTitle);
            setAdding(false);
            setEditing(null);
            rocks.reload();
            onChanged();
          }}
        />
      )}

      {heralding && <RockHerald title={heralding} onDone={() => setHeralding(null)} />}
    </>
  );
}

function RockRow({
  rock,
  onStatus,
  onEdit,
  onDelete,
  onMilestonesChanged,
  onError,
}: {
  rock: EosRock;
  onStatus: (status: RockStatus) => void;
  onEdit: () => void;
  onDelete: () => void;
  onMilestonesChanged: () => void;
  onError: (message: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [milestoneTitle, setMilestoneTitle] = useState('');

  const done = rock.milestones.filter((m) => m.IsDone).length;

  return (
    <div className="eos-row-block">
      <div className="row-between wrap" style={{ gap: 12 }}>
        <div className="grow">
          <div className="row wrap" style={{ gap: 8, alignItems: 'center' }}>
            {rock.IsCompanyRock && <span className="eos-pill neutral">Company</span>}
            <span className="strong">{rock.Title}</span>
          </div>
          <div className="xs subtle mt-2">
            {rock.OwnerName ?? 'Unassigned'}
            {rock.DueDate && ` · due ${formatDate(rock.DueDate)}`}
            {rock.milestones.length > 0 && ` · ${done} of ${rock.milestones.length} milestones done`}
          </div>
        </div>

        <div className="row wrap" style={{ gap: 4 }}>
          {/* The status dropdown IS the pill — one click per rock, coloured by
              what it now says. Rock Review in an L10 is exactly this: on track
              or off track, no discussion, next rock. A dialog per rock would
              make the five-minute section take twenty, and a separate chip
              beside the select would just say the same thing twice. */}
          <select
            className={`eos-status-select ${rockBand(rock.Status)}`}
            value={rock.Status}
            aria-label={`Status of ${rock.Title}`}
            onChange={(e) => onStatus(e.target.value as RockStatus)}
          >
            {STATUSES.map((s) => (
              <option key={s} value={s}>
                {ROCK_STATUS_LABELS[s]}
              </option>
            ))}
          </select>
          <button type="button" className="btn btn-ghost btn-sm" onClick={() => setOpen(!open)}>
            {open ? 'Hide' : 'Detail'}
          </button>
          <button type="button" className="btn btn-ghost btn-sm" onClick={onEdit}>
            Edit
          </button>
          <DeleteButton what={`the rock "${rock.Title}"`} onConfirm={onDelete} />
        </div>
      </div>

      {open && (
        <div className="mt-4">
          {rock.Description && <p className="small">{rock.Description}</p>}
          {rock.Smart && (
            <p className="small mt-2">
              <span className="xs subtle">SMART · </span>
              {rock.Smart}
            </p>
          )}

          <div className="mt-4">
            <div className="eos-section-title">Milestones</div>
            {rock.milestones.length === 0 && <p className="xs subtle">None yet.</p>}
            {rock.milestones.map((m) => (
              <label key={m.MilestoneId} className="checkbox eos-ms-row">
                <input
                  type="checkbox"
                  checked={m.IsDone}
                  onChange={(e) => {
                    void api
                      .put(`/eos/milestones/${m.MilestoneId}`, { isDone: e.target.checked })
                      .then(onMilestonesChanged)
                      .catch((err: unknown) =>
                        onError(err instanceof Error ? err.message : 'Could not update it.'),
                      );
                  }}
                />
                <span className={m.IsDone ? 'subtle' : ''}>
                  {m.Title}
                  {m.DueDate && <span className="xs subtle"> · {formatDate(m.DueDate)}</span>}
                </span>
                <DeleteButton
                  label="✕"
                  what={`the milestone "${m.Title}"`}
                  onConfirm={() => {
                    void api
                      .del(`/eos/milestones/${m.MilestoneId}`)
                      .then(onMilestonesChanged)
                      .catch((err: unknown) =>
                        onError(err instanceof Error ? err.message : 'Could not delete it.'),
                      );
                  }}
                />
              </label>
            ))}

            <form
              className="row mt-4"
              style={{ gap: 8 }}
              onSubmit={(e) => {
                e.preventDefault();
                if (!milestoneTitle.trim()) return;
                void api
                  .post(`/eos/rocks/${rock.RockId}/milestones`, { title: milestoneTitle.trim() })
                  .then(() => {
                    setMilestoneTitle('');
                    onMilestonesChanged();
                  })
                  .catch((err: unknown) =>
                    onError(err instanceof Error ? err.message : 'Could not add it.'),
                  );
              }}
            >
              <input
                className="input grow"
                placeholder="Add a milestone"
                value={milestoneTitle}
                onChange={(e) => setMilestoneTitle(e.target.value)}
              />
              <button type="submit" className="btn btn-secondary btn-sm">
                Add
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

function RockForm({
  teamId,
  quarter,
  employees,
  rock,
  onClose,
  onSaved,
}: {
  teamId: number;
  quarter: string;
  employees: Employee[];
  rock: EosRock | null;
  onClose: () => void;
  /** Given the rock's title when this save moved it into done, for the herald. */
  onSaved: (doneTitle: string | null) => void;
}) {
  const [title, setTitle] = useState(rock?.Title ?? '');
  const [description, setDescription] = useState(rock?.Description ?? '');
  const [smart, setSmart] = useState(rock?.Smart ?? '');
  const [ownerEmployeeId, setOwner] = useState<number | null>(rock?.OwnerEmployeeId ?? null);
  const [dueDate, setDueDate] = useState(rock?.DueDate?.slice(0, 10) ?? '');
  const [status, setStatus] = useState<RockStatus>(rock?.Status ?? 'on_track');
  const [isCompanyRock, setCompany] = useState(rock?.IsCompanyRock ?? false);
  const [businessUnitId, setBusinessUnit] = useState<number | null>(rock?.BusinessUnitId ?? null);
  const units = useResource<BusinessUnitOption[]>('/eos/business-units');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    if (!title.trim()) {
      setError('A rock needs a title.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const body = {
        title: title.trim(),
        description: description.trim() || null,
        smart: smart.trim() || null,
        ownerEmployeeId,
        dueDate: dueDate || null,
        status,
        isCompanyRock,
        /* A company rock belongs to no one team — it is reviewed by all of
           them — so the team is dropped rather than set to whichever list it
           happened to be created from. */
        teamId: isCompanyRock ? null : teamId,
        quarter,
        businessUnitId,
      };
      if (rock) await api.put(`/eos/rocks/${rock.RockId}`, body);
      else await api.post('/eos/rocks', body);
      onSaved(status === 'complete' && rock?.Status !== 'complete' ? body.title : null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save.');
      setSaving(false);
    }
  };

  return (
    <Modal
      title={rock ? 'Edit rock' : `Add rock for ${quarter}`}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn-ghost" onClick={onClose}>
            Cancel
          </button>
          <button type="button" className="btn btn-primary" disabled={saving} onClick={() => void submit()}>
            {saving ? 'Saving…' : 'Save'}
          </button>
        </>
      }
    >
      {error && <ErrorBanner message={error} />}
      <div className="form-grid">
        <Field label="Title">
          <input className="input" value={title} onChange={(e) => setTitle(e.target.value)} />
        </Field>
        <Field label="Owner" hint="One person, not a team — a rock with two owners has none.">
          <EmployeePicker employees={employees} value={ownerEmployeeId} onChange={setOwner} />
        </Field>
        <Field label="Due">
          <input
            className="input"
            type="date"
            value={dueDate}
            onChange={(e) => setDueDate(e.target.value)}
          />
        </Field>
        <Field
          label="Business unit"
          hint="For the business review. Left unset it follows the owner's own unit."
        >
          <BusinessUnitPicker
            units={units.data ?? []}
            value={businessUnitId}
            onChange={setBusinessUnit}
          />
        </Field>
        <Field label="Status">
          <select
            className="select"
            value={status}
            onChange={(e) => setStatus(e.target.value as RockStatus)}
          >
            {STATUSES.map((s) => (
              <option key={s} value={s}>
                {ROCK_STATUS_LABELS[s]}
              </option>
            ))}
          </select>
        </Field>
      </div>

      <label className="checkbox mt-4">
        <input
          type="checkbox"
          checked={isCompanyRock}
          onChange={(e) => setCompany(e.target.checked)}
        />
        <span>Company rock — reviewed in every team's meeting</span>
      </label>

      <div className="mt-4">
        <Field label="What it is">
          <textarea
            className="textarea"
            rows={3}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </Field>
      </div>
      <div className="mt-4">
        <Field
          label="SMART"
          hint="Specific, measurable, attainable, realistic, timely — the test for whether it landed."
        >
          <textarea
            className="textarea"
            rows={3}
            value={smart}
            onChange={(e) => setSmart(e.target.value)}
          />
        </Field>
      </div>
    </Modal>
  );
}
